import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { AbstractControl, ControlValueAccessor, FormArray, FormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { AuthorizationProcedureStatuses, cptCodeValidator } from '@mmx/shared'
import { map, Subject, takeUntil } from 'rxjs'

import { DataViewDialogComponent } from '../../../data-view-dialog'

export interface AuthCptPickerValue {
  [code: string]: {
    quantity?: number
    quantityNotApplicable?: boolean
    statusCode?: string
    rawData?: Record<string, any>
  }
}

@Component({
  selector: 'mmx-auth-cpt-picker',
  templateUrl: './auth-cpt-picker.component.html',
  styleUrls: ['./auth-cpt-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AuthCptPickerComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: AuthCptPickerComponent,
      multi: true,
    },
  ],
})
export class AuthCptPickerComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input()
  public emptyMessage = 'dialog.authorization.procedures.empty'

  @Input()
  public suggestedCodes: string[] = []

  @Input()
  public manuallyEditedFields: Set<string> = new Set()

  @Input()
  public preventAuthManualOverride = false

  @Input()
  public lockingField = false

  @Output()
  public unlockField = new EventEmitter<string>()

  @Output()
  public lockField = new EventEmitter<void>()

  private onDestroy$ = new Subject<void>()

  public formGroup = this.fb.group({
    items: this.fb.array([this.createItem()]),
  })

  public get items() {
    return this.formGroup.get('items') as FormArray
  }

  public authorizationProcedureStatuses = AuthorizationProcedureStatuses
  public codesRawDataMap: {
    [key: string]: Record<string, any> | undefined
  } = {}
  public savedItems = new Set<AbstractControl>()

  private onChange: any = () => { }
  private onTouched: any = () => { }
  private onValidatorChange: () => void = () => { }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) { }

  public ngOnInit() {
    this.formGroup.get('items')?.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        map(() => this.formatResponse()),
      )
      .subscribe((value) => {
        this.onChange?.(value)
        this.onTouched?.()
      })

    this.formGroup.statusChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.onValidatorChange?.()
      })
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public get missingCodes(): string[] {
    const codesOnAuth = new Set(
      this.items.controls
        .map((control) => control.get('code')?.value)
        .filter(Boolean),
    )

    return (this.suggestedCodes ?? []).filter((code) => code && !codesOnAuth.has(code))
  }

  public addItem() {
    this.items.push(this.createItem(null, 1))
  }

  public addMissingCodes() {
    this.missingCodes.forEach((code) => this.items.push(this.createItem(code, 1)))
  }

  public removeItem(index: number) {
    this.items.removeAt(index)
  }

  public writeValue(value: AuthCptPickerValue): void {
    const knownRawData = this.codesRawDataMap

    this.items.clear()
    this.codesRawDataMap = {}

    if (value) {
      Object.entries(value).forEach(([code, { quantity, quantityNotApplicable, statusCode, rawData }]) => {
        this.codesRawDataMap[code] = rawData ?? knownRawData[code]
        this.items.push(this.createItem(code, quantity, statusCode, quantityNotApplicable))
      })
    }

    this.cdr.markForCheck()
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  public registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn
  }

  public validate() {
    if (this.items.length === 0) {
      return { required: true }
    }

    const errors: ValidationErrors = {}
    const codes = new Set<string>()
    this.items.controls.forEach((control: AbstractControl, index: number) => {
      const { code } = control.value

      if (!code) {
        errors[`missingCode-${index}`] = { message: 'Missing code' }
      } else if (control.get('code').invalid) {
        errors[`invalidCode-${index}`] = { message: 'Invalid code' }
      } else if (codes.has(code)) {
        errors[`duplicateCode-${index}`] = { message: 'Duplicate code' }
      } else {
        codes.add(code)
      }
    })

    if (Object.keys(errors).length) {
      return errors
    }

    return this.formGroup.invalid ? { invalid: true } : null
  }

  public createItem(
    code: string | null = null,
    quantity: number | null = null,
    statusCode: string | null = null,
    quantityNotApplicable = false,
  ) {
    const item = this.fb.group({
      code: [code, [Validators.required, cptCodeValidator()]],
      quantity: [quantity ?? null, (ctrl: AbstractControl) => {
        const v = ctrl.value
        const notApplicable = ctrl.parent?.get('quantityNotApplicable')?.value
        if (notApplicable) {
          return null
        }
        return v != null && v < 0 ? { min: true } : null
      }],
      statusCode: [statusCode],
      quantityNotApplicable: [quantityNotApplicable],
    })

    const quantityControl = item.get('quantity')
    const quantityNotApplicableControl = item.get('quantityNotApplicable')

    if (quantityNotApplicableControl?.value) {
      quantityControl?.disable({ emitEvent: false })
    }

    quantityNotApplicableControl?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isNotApplicable) => {
        if (isNotApplicable) {
          quantityControl?.disable()
        } else {
          quantityControl?.enable()
        }
      })

    return item
  }

  public getCode(index: number): string | null {
    return this.items.at(index)?.get('code')?.value ?? null
  }

  public getRawData(index: number) {
    const code = this.items.at(index)?.get('code')?.value

    return this.codesRawDataMap[code]
  }

  public viewRawData({ data, index }: { data: Record<string, any>, index: number }) {
    this.dialog.open(DataViewDialogComponent, {
      data: {
        dataToDisplay: data,
        title: `${this.items.at(index)?.get('code')?.value} Data`,
      },
      width: '600px',
      panelClass: 'dialog-form',
    })
  }

  private formatResponse() {
    const items = this.formGroup.get('items')?.getRawValue() as {
        code: string | null;
        quantity: number | null;
        statusCode: string | null;
        quantityNotApplicable: boolean | null;
    }[]

    return items.filter(item => item.code)
      .reduce((acc, { code, quantity, statusCode, quantityNotApplicable }) => ({
        ...acc,
        [code!]: {
          quantity,
          statusCode,
          quantityNotApplicable: !!quantityNotApplicable,
        },
      }), {})
  }
}
